// pattern: imperative-shell
import { clearToken } from "./storage";

let _token = "";

export function setApiToken(token: string): void {
	_token = token;
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
	const res = await fetch(`/api${path}`, {
		...options,
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${_token}`,
			...options.headers,
		},
	});

	if (res.status === 401) {
		clearToken();
		window.location.reload();
		throw new Error("unauthorized");
	}

	const data = await res.json();

	if (!res.ok) {
		throw new Error((data as { error?: string }).error ?? `HTTP ${res.status}`);
	}

	return data as T;
}

export const api = {
	// Locations
	getLocations: () => request<Location[]>("/locations"),
	createLocation: (body: unknown) =>
		request<{ id: string }>("/locations", { method: "POST", body: JSON.stringify(body) }),
	updateLocation: (id: string, body: unknown) =>
		request<unknown>(`/locations?id=${id}`, { method: "PUT", body: JSON.stringify(body) }),
	deleteLocation: (id: string) => request<unknown>(`/locations?id=${id}`, { method: "DELETE" }),

	// Observations
	getObservations: (params?: Record<string, string>) => {
		const qs = params ? `?${new URLSearchParams(params)}` : "";
		return request<Observation[]>(`/observations${qs}`);
	},
	createObservation: (body: unknown) =>
		request<{ id: string }>("/observations", { method: "POST", body: JSON.stringify(body) }),
	updateObservation: (id: string, body: unknown) =>
		request<unknown>(`/observations?id=${id}`, { method: "PUT", body: JSON.stringify(body) }),
	deleteObservation: (id: string) =>
		request<unknown>(`/observations?id=${id}`, { method: "DELETE" }),

	// Weather
	getWeather: (from: string, to: string) => request<Weather[]>(`/weather?from=${from}&to=${to}`),
	fetchWeather: (from?: string, to?: string) =>
		request<{ fetched: number }>("/weather-fetch", {
			method: "POST",
			body: JSON.stringify(from && to ? { from, to } : {}),
		}),

	// NL parsing
	parseNaturalLanguage: (text: string) =>
		request<ParseResult>("/parse", { method: "POST", body: JSON.stringify({ text }) }),

	// NL query
	queryNaturalLanguage: (question: string) =>
		request<QueryResult>("/query", {
			method: "POST",
			body: JSON.stringify({ question }),
		}),

	// Photo identification
	identifyPhoto: (imageBase64: string, mimeType: string) =>
		request<PhotoIdentification>("/identify", {
			method: "POST",
			body: JSON.stringify({ image: imageBase64, mime_type: mimeType }),
		}),
};

// Types used by the API client (mirrors DB shape + joins)
interface Location {
	id: string;
	name: string;
	light: string;
	notes: string | null;
	created_at: number;
	updated_at: number;
}

interface Observation {
	id: string;
	observed_date: string;
	plant_name: string;
	location_id: string;
	location_name: string;
	location_light: string;
	stage: string;
	notes: string | null;
	raw_input: string | null;
	created_at: number;
	updated_at: number;
}

interface Weather {
	date: string;
	temp_high_c: number | null;
	temp_low_c: number | null;
	precip_mm: number | null;
	conditions: string | null;
	wind_max_kmh: number | null;
}

interface ParsedObservationResult {
	plant_name: string;
	location_name: string | null;
	location_id: string | null;
	stage: string;
	observed_date: string;
	notes: string | null;
}

interface ParseResult {
	observations: ParsedObservationResult[];
	raw_input: string;
}

interface QueryResult {
	question: string;
	explanation: string;
	sql: string;
	results: Record<string, unknown>[];
	count: number;
}

interface PhotoIdentification {
	plant_name: string;
	scientific_name: string | null;
	stage: string | null;
	is_seed_packet: boolean;
	variety: string | null;
	notes: string;
	confidence: "high" | "medium" | "low";
}

export type {
	Location as ApiLocation,
	Observation as ApiObservation,
	Weather as ApiWeather,
	ParseResult as ApiParseResult,
	ParsedObservationResult as ApiParsedObservation,
	QueryResult as ApiQueryResult,
	PhotoIdentification as ApiPhotoId,
};
